import type {
  ExpensesEntitiesExpensableStatusEnum,
  ExpensesExpensableFilter,
  IntegrationsAgreggatedSyncableStateValuesEnum,
} from 'generated/resources/graphql'

export interface ExpensesFilters {
  status?: ExpensesEntitiesExpensableStatusEnum[]
  employeeIds?: number[]
  legalEntityIds?: number[]
  creationType?: ('automatic' | 'manual')[]
  documentType?: string[]
  currency?: string[]
  grouped?: string[]
  reimbursable?: string[]
  reimbursableCurrency?: string[]
  category?: string[]
  subcategory?: string[]
  spendingAlertType?: string[]
  pendingApproval?: string[]
  approvedAt?: { from?: Date | string; to?: Date | string }
  reviewRequestAt?: { from?: Date | string; to?: Date | string }
  internalReference?: string[]
  project?: number[]
  subproject?: number[]
  reportedBy?: string[]
}

/**
 * Factory function to create ID parsers
 * Converts string/number values to number array, filtering out invalid IDs
 */
const createIdParser = (_name: string) => {
  return (value: unknown): number[] | undefined => {
    if (!value || !Array.isArray(value) || value.length === 0) {
      return undefined
    }

    const parsed = value.map((id) => Number(id)).filter((id) => !isNaN(id) && id > 0)

    return parsed.length > 0 ? parsed : undefined
  }
}

const parseEmployeeIds = createIdParser('employee')
const parseLegalEntityIds = createIdParser('legalEntity')
const parseProjectIds = createIdParser('project')
const parseSubprojectIds = createIdParser('subproject')

function parseStatusFilter(value: unknown): ExpensesEntitiesExpensableStatusEnum[] | undefined {
  if (!value || !Array.isArray(value) || value.length === 0) {
    return undefined
  }
  // GraphQL will validate enum values at runtime
  return value
}

function parseGroupedFilter(value: unknown): boolean | undefined {
  if (!value || !Array.isArray(value) || value.length === 0) {
    return undefined
  }
  if (value.length > 1) return undefined // Both grouped and ungrouped options are selected

  return value[0] === 'true'
}

function parseIntegrationsSyncableStates(
  value: unknown
): IntegrationsAgreggatedSyncableStateValuesEnum[] | undefined {
  if (!value || !Array.isArray(value) || value.length === 0) {
    return undefined
  }
  // GraphQL will validate enum values at runtime
  return value
}

const sanitizeSearchTerm = (term: string): string => {
  return term.trim()
}

/**
 * Builds the ExpensesExpensableFilter object for the GraphQL query
 * Converts UI filter values to the expected GraphQL filter structure
 */
const buildExpensableFilter = (
  filters: ExpensesFilters | undefined
): ExpensesExpensableFilter | undefined => {
  if (!filters) return undefined

  const legalEntityIds = parseLegalEntityIds(filters.legalEntityIds)
  const projectIds = parseProjectIds(filters.project)
  const subprojectIds = parseSubprojectIds(filters.subproject)

  const getDateRange = (dateValue: { from?: Date | string; to?: Date | string } | undefined) => {
    if (!dateValue?.from || !dateValue?.to) return undefined

    const fromStr =
      dateValue.from instanceof Date ? dateValue.from.toISOString().split('T')[0] : dateValue.from
    const toStr =
      dateValue.to instanceof Date ? dateValue.to.toISOString().split('T')[0] : dateValue.to

    return { between: [fromStr, toStr] }
  }

  // Build filter object in a single pass, filtering out undefined values
  const filterEntries = Object.entries({
    // StringFilter with 'eq' for single selection
    creationType: filters.creationType?.[0] ? { eq: filters.creationType[0] } : undefined,
    pendingApproval: filters.pendingApproval?.[0] ? { eq: filters.pendingApproval[0] } : undefined,
    reportedBy: filters.reportedBy?.[0] ? { eq: filters.reportedBy[0] } : undefined,

    // StringFilter with 'in' for multi-selection
    documentType: filters.documentType?.length ? { in: filters.documentType } : undefined,
    currency: filters.currency?.length ? { in: filters.currency } : undefined,
    reimbursableCurrency: filters.reimbursableCurrency?.length
      ? { in: filters.reimbursableCurrency }
      : undefined,
    category: filters.category?.length ? { in: filters.category } : undefined,
    subcategory: filters.subcategory?.length ? { in: filters.subcategory } : undefined,
    spendingAlertType: filters.spendingAlertType?.length
      ? {
          in: filters.spendingAlertType.flatMap((type) =>
            type === 'out_of_policy' ? ['spending_policy', 'policy_compliance'] : [type]
          ),
        }
      : undefined,

    // Status filter (for Groups queries that don't support top-level status parameter)
    status: filters.status?.length ? { in: filters.status } : undefined,

    // StringFilter with 'contains' for text search
    internalReference: filters.internalReference?.[0]
      ? { contains: sanitizeSearchTerm(filters.internalReference[0]) }
      : undefined,

    // IntegerFilter with 'in' for IDs
    legalEntity: legalEntityIds?.length ? { in: legalEntityIds } : undefined,
    project: projectIds?.length ? { in: projectIds } : undefined,
    subproject: subprojectIds?.length ? { in: subprojectIds } : undefined,

    // BooleanFilter with 'eq'
    reimbursable: filters.reimbursable?.[0]
      ? { eq: filters.reimbursable[0] === 'true' }
      : undefined,

    // DateFilter with 'between' for date ranges
    approvedAt: getDateRange(filters.approvedAt),
    reviewRequestAt: getDateRange(filters.reviewRequestAt),
  }).filter(([_, value]) => value !== undefined)

  return filterEntries.length > 0 ? Object.fromEntries(filterEntries) : undefined
}

/**
 * Maps DataCollection filter state to GraphQL query variables
 *
 * The backend has two different query structures:
 * 1. Expenses queries (ExpensablesDataCollection):
 *    - status: as a direct/top-level parameter
 *    - employeeIds: as a direct parameter
 *    - filter: for other filters
 *
 * 2. Groups queries (ExpensableGroupsDataCollection):
 *    - employeeIds: as a direct parameter
 *    - filter: for all filters INCLUDING status (no top-level status parameter)
 *
 * This function returns status in BOTH locations to support both query types.
 * Each component uses whichever parameters their GraphQL query supports.
 */
export const mapFiltersToVariables = (
  filters: Record<string, unknown> | undefined,
  options: { isGroupView?: boolean } = {}
) => {
  return {
    status: parseStatusFilter(filters?.status),
    employeeIds: parseEmployeeIds(filters?.employeeIds),
    grouped: options.isGroupView ? undefined : parseGroupedFilter(filters?.grouped),
    filter: buildExpensableFilter(filters),
    integrationsSyncableStates: parseIntegrationsSyncableStates(
      filters?.integrationsAgreggatedSyncableStates
    ),
  }
}
